import { ReactNode, FC } from 'react'
import { toast, useToaster, resolveValue } from 'react-hot-toast/headless'

import withRenderClientsideOnly from '../utils/withRenderClientsideOnly'
import ToastBody, { Appearance } from './Toast'

const DEFAULT_DURATION = 7000

type ToastOptions = {
  appearance?: Appearance
  autoDismiss?: boolean
  autoDismissTimeout?: number
}

const renderToast = (
  message: ReactNode,
  options: ToastOptions = {},
): string => {
  const {
    appearance = 'success',
    autoDismiss = true,
    autoDismissTimeout = DEFAULT_DURATION,
  } = options
  const duration = autoDismiss ? autoDismissTimeout : Infinity

  return toast.custom(
    (t) => (
      <ToastBody t={t} appearance={appearance}>
        {message}
      </ToastBody>
    ),
    { duration },
  )
}

// Shim mirroring the react-toast-notifications API surface so the call sites
// can keep their existing `useToasts().addToast(msg, opts)` shape. Only
// `addToast` is consumed by production code today; the other methods are
// kept as placeholders to satisfy the `withToasts` HOC contract.
export const useToasts = () => ({
  addToast: renderToast,
  removeToast: (id: string) => toast.dismiss(id),
  removeAllToasts: () => toast.dismiss(),
  updateToast: () => {},
  toastStack: [] as Array<unknown>,
})

// We use react-hot-toast's headless entry point so the package's goober-based
// stylesheet injection (which calls document.createElement at module load)
// never runs on the Rails react_on_rails SSR environment. We render the
// container ourselves and rely on toast.custom() for the per-toast markup.
//
// The container is gated to client-only with withRenderClientsideOnly so the
// SSR output and first-client-paint output match (both render nothing here);
// the Toaster then mounts after hydration. Children are a *sibling* of the
// container, so their tree position is stable across server/client — this is
// not the parent-type-swap pattern that reverted in a753fc28c0 / 4b403597db.
const ToasterContainer = withRenderClientsideOnly(() => {
  const { toasts } = useToaster({ duration: DEFAULT_DURATION })

  return (
    <div
      className="riparide-toaster"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        maxWidth: 360,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <div key={t.id} style={{ pointerEvents: 'auto' }}>
          {resolveValue(t.message, t)}
        </div>
      ))}
    </div>
  )
})

const ToastProvider: FC<{ children?: ReactNode }> = ({ children }) => (
  <>
    {children}
    <ToasterContainer />
  </>
)

export const TestToastProvider: FC<{ children?: ReactNode }> = ({
  children,
}) => (
  <>
    {children}
    <ToasterContainer />
  </>
)

export default ToastProvider
